import React, { useCallback, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import SydModal from '../../../../commonDesign/SydModal'
import SydModalActions from '../../../../commonDesign/SydModal/SydModalActions'
import SydButton from '../../../../commonDesign/Button'
import SydSelect from '../../../../commonDesign/SydSelect'
import SydInput from '../../../../commonDesign/SydInput'
import SydLabel from '../../../../commonDesign/SydLabel'
import { CUSTODIAL_CASH_OPTIONS } from './constants'
import TransactionTypeTree from './TransactionTypeTree'

const EMPTY_STATE = {
  transactionTypes: [],
  iscustodialcash: '',
  transamountMin: null,
  transamountMax: null,
  managedFilter: ''
}

function countActiveFilters (state) {
  let count = 0
  if (state.transactionTypes?.length) count++
  if (state.iscustodialcash) count++
  if (state.transamountMin != null || state.transamountMax != null) count++
  if (state.managedFilter) count++
  return count
}

export { countActiveFilters }

const bodyLayout = {
  display: 'flex',
  gap: '24px',
  padding: '8px 0',
  height: 'calc(70vh - 120px)',
  minHeight: '300px',
  maxHeight: '600px'
}

const leftBlade = {
  flex: '0 0 30%',
  display: 'flex',
  flexDirection: 'column',
  gap: '20px'
}

const divider = {
  width: '1px',
  background: 'var(--color-neutral-100)',
  flexShrink: 0
}

const rightBlade = {
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  minWidth: 0,
  minHeight: 0,
  overflow: 'hidden'
}

const rightHeader = {
  fontWeight: 600,
  fontSize: 'var(--font-size-md)',
  marginBottom: '8px'
}

export default function SyncedTransactionsFilterModal ({ open, onClose, filterState, availableFilters, onApply }) {
  const [local, setLocal] = useState(EMPTY_STATE)
  const af = availableFilters ?? {}

  useEffect(() => {
    if (open) setLocal({ ...EMPTY_STATE, ...filterState })
  }, [open, filterState])

  const patch = useCallback((update) => {
    setLocal(prev => ({ ...prev, ...update }))
  }, [])

  const handleCustodialCash = useCallback((e) => {
    patch({ iscustodialcash: e.target.value })
  }, [patch])

  const handleAmountMin = useCallback((e) => {
    const raw = e.target.value
    patch({ transamountMin: raw === '' ? null : Number(raw) })
  }, [patch])

  const handleAmountMax = useCallback((e) => {
    const raw = e.target.value
    patch({ transamountMax: raw === '' ? null : Number(raw) })
  }, [patch])

  const handleTransactionTypesChange = useCallback((types) => {
    patch({ transactionTypes: types })
  }, [patch])

  const handleApply = useCallback(() => {
    onApply(local)
    onClose()
  }, [local, onApply, onClose])

  const handleClear = useCallback(() => {
    onApply(EMPTY_STATE)
    onClose()
  }, [onApply, onClose])

  const showLeftBlade = af.iscustodialcash || af.transamount || af.managedFilter
  const showRightBlade = af.transactionTypes !== false

  return (
    <SydModal
      title='Transaction Filters'
      open={open}
      onClose={onClose}
      size='large'
    >
      <div style={bodyLayout}>
        {showLeftBlade ? (
          <div style={leftBlade}>
            {af.iscustodialcash ? (
              <SydLabel label='Custodial Cash'>
                <SydSelect
                  minWidth='0px'
                  options={CUSTODIAL_CASH_OPTIONS}
                  value={local.iscustodialcash}
                  onChange={handleCustodialCash}
                />
              </SydLabel>
            ) : null}

            {af.transamount ? (
              <>
                <SydLabel label='Min Amount'>
                  <SydInput
                    type='number'
                    minWidth='0px'
                    value={local.transamountMin ?? ''}
                    onChange={handleAmountMin}
                    placeholder='Min'
                  />
                </SydLabel>
                <SydLabel label='Max Amount'>
                  <SydInput
                    type='number'
                    minWidth='0px'
                    value={local.transamountMax ?? ''}
                    onChange={handleAmountMax}
                    placeholder='Max'
                  />
                </SydLabel>
              </>
            ) : null}

            {af.managedFilter ? (
              <SydLabel label='Asset Type'>
                <SydSelect
                  minWidth='0px'
                  options={[
                    { value: '', label: 'Component Default' },
                    { value: 'all', label: 'All Assets' },
                    { value: 'managed', label: 'Managed Only' },
                    { value: 'unmanaged', label: 'Unmanaged Only' }
                  ]}
                  value={local.managedFilter || ''}
                  onChange={(e) => patch({ managedFilter: e.target.value })}
                />
              </SydLabel>
            ) : null}
          </div>
        ) : null}

        {showLeftBlade && showRightBlade ? <div style={divider} /> : null}

        {showRightBlade ? (
          <div style={rightBlade}>
            <div style={rightHeader}>Transaction Types</div>
            <TransactionTypeTree
              selectedTypes={local.transactionTypes ?? []}
              onChange={handleTransactionTypesChange}
            />
          </div>
        ) : null}
      </div>

      <SydModalActions>
        <SydButton variant='ghost' size='md' onClick={handleClear}>
          Clear All
        </SydButton>
        <SydButton variant='primary' size='md' onClick={handleApply}>
          Apply
        </SydButton>
      </SydModalActions>
    </SydModal>
  )
}

SyncedTransactionsFilterModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  filterState: PropTypes.object.isRequired,
  availableFilters: PropTypes.object,
  onApply: PropTypes.func.isRequired
}
